<template>
  <div class="home-layout">
    <div class="home-content p-3 p-md-4">

      <BRow class="g-3 g-md-4">
        <BCol v-for="card in visibleCards" :key="card.title" cols="12" md="6" xl="4">
          <BCard
              class="h-100 text-center shadow-sm border-0 rounded-4"
              body-class="d-flex flex-column justify-content-center align-items-center p-4"
              style="cursor:pointer"
              @click="card.action()"
          >
            <div class="fs-1 mb-3">{{ card.icon }}</div>
            <h5 class="fw-semibold mb-2">{{ card.title }}</h5>
            <p class="text-muted mb-0">{{ card.description }}</p>
          </BCard>
        </BCol>
      </BRow>

    </div>
    <aside
        class="notices-panel"
    >
      <h5 class="fw-bold text-center mb-3">
        📢 Avisos do dia
      </h5>
      <small class="text-muted d-block text-center mb-2">
        Clique em um aviso para visualizar
      </small>


      <div class="notices-scroll">

        <div
            v-for="notice in notices"
            :key="notice.id"
            class="notice-card p-3 mb-3"
            title="Clique para visualizar o aviso"
            @click="openNotice(notice)"
        >
          <div class="flex-grow-1 me-2">
            <div class="fw-semibold mb-1 text-truncate">
              {{ notice.title }}
              <span class="notice-arrow">
                &rsaquo;
            </span>
            </div>

            <small class="text-muted notice-description">
              {{ notice.information }}
            </small>

          </div>
        </div>

        <div
            v-if="notices.length === 0"
            class="text-center text-muted py-5"
        >
          <div style="font-size:32px">📭</div>
          <small>Nenhum aviso no momento</small>
        </div>

      </div>
    </aside>

    <div
        v-if="showNoticeView"
        class="informationview-popup-wrapper"
        @click.self="closeNoticeView"
    >
      <BCard class="information-view-card">

        <template #header>
          <div class="d-flex justify-content-between align-items-center">

            <h5 class="mb-0">
              {{ selectedNotice?.title }}
            </h5>

            <BButton
                variant="outline-secondary"
                size="sm"
                @click="closeNoticeView"
            >
              &times;
            </BButton>

          </div>
        </template>

        <div class="information-content">
          {{ selectedNotice?.information }}
        </div>

      </BCard>
    </div>

  </div>
</template>

<script>
import { computed } from "vue"
import {useRouter} from "vue-router"
import {BRow, BCol, BCard, BButton} from "bootstrap-vue-next"
import {getInvestorId, getRole} from "@/service/authService"
import informationService from "@/service/information/informationService"

export default {
  name: "HomeView",
  components: {
    BButton,
    BRow,
    BCol,
    BCard
  },
  setup() {
    const router = useRouter()

    const role = (getRole() || "").toLowerCase()
    const canSeeRestrictedCards = role === "admin" || role === "employee"
    const canSeeInvestorCards = role === "investor"

    const go = (path) => {
      router.push(path)
    }

    const goToInvestmentCatalog = () => {
      router.push({name: "investment-catalog"})
    }

    const goToMyDeposits = () => {
      const investorId = getInvestorId()

      if (!investorId) return

      router.push({
        path: `/depositos/${investorId}`
      })
    }

    const goToWallet = () => {
      const investorId = getInvestorId()

      if (!investorId) return

      router.push({
        name: "wallet",
        params: {investorId}
      })
    }

    const buildCurrentYearRange = () => {
      const currentYear = new Date().getFullYear()
      return {
        start: `${currentYear}-01-01`,
        end: `${currentYear}-12-31`
      }
    }

    const LOGIN_METRICS_FIXED_USER_ID = "c8a60158-cabe-4c48-a0b6-d3a1667c82ea"

    const goToLoginMetrics = () => {
      const { start, end } = buildCurrentYearRange()
      const userId = LOGIN_METRICS_FIXED_USER_ID

      router.push({
        path: `/metricas-login/${encodeURIComponent(userId)}/${encodeURIComponent(start)}/${encodeURIComponent(end)}`
      })
    }

    const adminEmployeeCards = [
      { title: "Avisos", description: "Visualizar avisos do dia", icon: "📢", action: () => go("/avisos") },
      { title: "Investidores", description: "Gerenciar investidores", icon: "👤", action: () => go("/investidores") },
      { title: "Investimentos disponíveis", description: "Ver investimentos disponíveis", icon: "📈", action: goToInvestmentCatalog },
      { title: "Gerenciar investimentos", description: "Administrar investimentos", icon: "⚙️", action: () => go("/investimentos") },
      { title: "Meus depósitos", description: "Ver meus depósitos", icon: "💵", action: goToMyDeposits },
      { title: "Todos os depósitos", description: "Ver todos os depósitos", icon: "💰", action: () => go("/depositos/todos") },
      { title: "Minha carteira", description: "Ver minha carteira", icon: "💼", action: goToWallet },
      { title: "Devoluções", description: "Acompanhar devoluções", icon: "🔁", action: () => go("/devolucoes") },
      { title: "Histórico de pagamentos", description: "Consultar pagamentos", icon: "🧾", action: () => go("/historico-pagamentos") },
      { title: "Métricas de login", description: "Visualizar acessos", icon: "📊", action: goToLoginMetrics }
    ]

    const investorCards = [
      { title: "Avisos", description: "Visualizar avisos do dia", icon: "📢", action: () => go("/avisos") },
      { title: "Investimentos disponíveis", description: "Ver investimentos disponíveis", icon: "📈", action: goToInvestmentCatalog },
      { title: "Meus depósitos", description: "Ver meus depósitos", icon: "💵", action: goToMyDeposits },
      { title: "Devoluções", description: "Acompanhar devoluções", icon: "🔁", action: () => go("/devolucoes") },
      { title: "Histórico de pagamentos", description: "Consultar pagamentos", icon: "🧾", action: () => go("/historico-pagamentos") },
      { title: "Minha carteira", description: "Ver minha carteira", icon: "💼", action: goToWallet }
    ]

    const visibleCards = computed(() => {
      if (canSeeRestrictedCards) return adminEmployeeCards
      if (canSeeInvestorCards) return investorCards
      return []
    })

    return {
      visibleCards
    }
  },

  data() {
    return {
      notices: [],
      loadingNotices: false,
      selectedNotice: null,
      showNoticeView: false,
    }
  },

  beforeRouteEnter(_to, _from, next) {
    next(vm => {
      vm.loadNotices()
    })
  },

  async beforeRouteUpdate() {
    await this.loadNotices()
  },

  methods: {
    async loadNotices() {
      this.loadingNotices = true

      try {
        const response = await informationService.findActive(
            0,
            10
        )

        this.notices = response?.content || []

      } catch (e) {
        console.error(e)
        this.notices = []
      } finally {
        this.loadingNotices = false
      }
    },
    async openNotice(notice) {
      this.selectedNotice = notice
      this.showNoticeView = true
    },

    closeNoticeView() {
      this.showNoticeView = false
      this.selectedNotice = null
    },

    formatDate(d) {
      if (!d) return "-"
      try {
        return new Date(d).toLocaleDateString("pt-BR")
      } catch (e) {
        return d
      }
    }
  }
}
</script>
<style>
.home-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.home-content {
  flex: 1;
}

.notices-panel {
  width: 280px;
  flex-shrink: 0;

  position: sticky;
  top: 80px;

  height: calc(100vh - 100px);

  padding: 16px;
  background: #fff;
  border-left: 1px solid #dee2e6;
}

.notices-scroll {
  padding-top: 6px;
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.notice-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  cursor: pointer;
  transition: .2s;
}

.notice-card:hover {
  background: #eef5ff;
  border-color: #0d6efd;
}


@media (max-width: 992px) {

  .home-layout {
    display: flex;
    flex-direction: column;
  }

  .notices-panel {
    order: -1;          /* fica antes da Home */
    position: static;
    width: 100%;
    height: auto;

    border-left: none;
    border-bottom: 1px solid #dee2e6;

    padding: 16px;
    margin-bottom: 16px;
  }

  .home-content {
    width: 100%;
  }

  .notices-scroll {
    max-height: 300px;
  }
}

.informationview-popup-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.35);
  padding: 1rem;
}

.information-view-card {
  width: 100%;
  max-width: 700px;
  max-height: 80vh;
  overflow: auto;
}

.information-content {
  white-space: pre-line;
  word-break: break-word;
  text-align: justify;
  line-height: 1.6;
}

.notice-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  white-space: pre-line;
  text-align: justify;
  text-justify: inter-word;
  line-height: 1.45;
  word-break: break-word;
}

.notice-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  cursor: pointer;
  transition: all .2s ease;
}

.notice-card:hover {
  background: #eef5ff;
  border-color: #0d6efd;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 110, 253, .15);
}

.notice-arrow {
  font-size: 1.5rem;
  color: #6c757d;
  transition: all .2s ease;
}

.notice-card:hover .notice-arrow {
  color: #0d6efd;
  transform: translateX(4px);
}

</style>
